import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';
import { ENROLL_EVENT_REPORT } from 'src/app/@constants/local-storage-key-constants';
import { EnrollEventReport } from 'src/app/@models/analytics/enroll-event-report.model';
import { getLocalStorageItem } from 'src/app/@utils/local-storage-util';
import { WINDOW } from 'src/app/@utils/window.token';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor() {
    this._initializeUserFromStorage();
    this._startSession();
    this._window?.addEventListener('beforeunload', () =>
      this._trackSessionEnd()
    );
  }

  private readonly _document = inject(DOCUMENT);
  private readonly _window = inject(WINDOW);

  private readonly _userId = signal<string | null>(null);
  private readonly _userEmailDomain = signal<string | null>(null);
  private readonly _sessionStartTime = signal<number | null>(null);
  private readonly _enrollmentData = signal<EnrollEventReport | null>(null);

  private readonly _userParams = computed(() => ({
    user_id: this._userId() ?? 'anonymous',
    user_email_domain: this._userEmailDomain() ?? 'unknown',
    has_email: this._userId() ? 'true' : 'false',
    event_name: this._enrollmentData()?.eventNameIdentifier ?? 'unknown',
    wants_notifications:
      this._enrollmentData()?.sendEventReportNotification ?? false,
  }));

  setUserId(email: string): void {
    if (!this._window) {
      return;
    }

    this._userId.set(btoa(email));
    this._userEmailDomain.set(email.split('@')[1]);

    this._window.gtag('config', 'G-X98B15C2YG', {
      ...this._userParams(),
      send_page_view: true,
      cookie_flags: 'max-age=7200;secure;samesite=none',
    });

    this._window.gtag('set', {
      ...this._userParams(),
      first_visit_timestamp: new Date().toISOString(),
      user_agent: this._window.navigator.userAgent,
      screen_resolution: `${this._window.screen.width}x${this._window.screen.height}`,
      viewport_size: `${this._window.innerWidth}x${this._window.innerHeight}`,
      language: this._window.navigator.language,
    });
  }

  trackPageView(pagePath: string, pageTitle: string): void {
    if (!this._window) {
      return;
    }

    this._window.gtag('event', 'page_view', {
      ...this._userParams(),
      page_path: pagePath,
      page_title: pageTitle,
      page_referrer: this._document.referrer,
      timestamp: new Date().toISOString(),
      viewport_size: `${this._window.innerWidth}x${this._window.innerHeight}`,
      engagement_time_msec: this._getTimeOnPage(),
    });
  }

  trackEvent(
    eventName: string,
    eventParams: Record<string, unknown> = {}
  ): void {
    if (!this._window) {
      return;
    }

    this._window.gtag('event', eventName, {
      ...this._userParams(),
      ...eventParams,
      timestamp: new Date().toISOString(),
      page_location: this._window.location.href,
      page_title: this._document.title,
    });
  }

  trackEngagement(engagementTime: number, engagementType: string): void {
    if (!this._window) {
      return;
    }

    this._window.gtag('event', 'user_engagement', {
      ...this._userParams(),
      engagement_time_msec: engagementTime * 1000,
      engagement_type: engagementType,
      timestamp: new Date().toISOString(),
      page_location: this._window.location.href,
      page_title: this._document.title,
    });
  }

  trackScrollDepth(scrollDepth: number): void {
    if (!this._window) {
      return;
    }

    this._window.gtag('event', 'scroll', {
      ...this._userParams(),
      scroll_depth: scrollDepth,
      page_location: this._window.location.href,
      timestamp: new Date().toISOString(),
    });
  }

  trackDownload(fileName: string, fileType: string): void {
    if (!this._window) {
      return;
    }

    this._window.gtag('event', 'file_download', {
      ...this._userParams(),
      file_name: fileName,
      file_type: fileType,
      timestamp: new Date().toISOString(),
    });
  }

  trackFormInteraction(
    formId: string,
    action: 'start' | 'complete' | 'error',
    details?: Record<string, unknown>
  ): void {
    if (!this._window) {
      return;
    }

    this._window.gtag('event', 'form_' + action, {
      ...this._userParams(),
      form_id: formId,
      form_name: formId,
      ...details,
      timestamp: new Date().toISOString(),
    });
  }

  trackClick(
    elementId: string,
    elementType: string,
    elementContent?: string
  ): void {
    if (!this._window) {
      return;
    }

    this._window.gtag('event', 'click', {
      ...this._userParams(),
      element_id: elementId,
      element_type: elementType,
      element_content: elementContent,
      page_location: this._window.location.href,
      timestamp: new Date().toISOString(),
    });
  }

  trackError(
    errorType: string,
    errorMessage: string,
    errorStack?: string
  ): void {
    if (!this._window) {
      return;
    }

    this._window.gtag('event', 'error', {
      ...this._userParams(),
      error_type: errorType,
      error_message: errorMessage,
      error_stack: errorStack,
      page_location: this._window.location.href,
      timestamp: new Date().toISOString(),
    });
  }

  trackSearch(searchTerm: string, resultCount: number): void {
    if (!this._window) {
      return;
    }

    this._window.gtag('event', 'search', {
      ...this._userParams(),
      search_term: searchTerm,
      result_count: resultCount,
      timestamp: new Date().toISOString(),
    });
  }

  trackVideoInteraction(
    videoId: string,
    action: 'play' | 'pause' | 'complete',
    currentTime: number
  ): void {
    if (!this._window) {
      return;
    }

    this._window.gtag('event', 'video_' + action, {
      ...this._userParams(),
      video_id: videoId,
      current_time: currentTime,
      timestamp: new Date().toISOString(),
    });
  }

  private _initializeUserFromStorage(): void {
    const storedData =
      getLocalStorageItem<EnrollEventReport>(ENROLL_EVENT_REPORT);
    if (storedData?.email) {
      this._enrollmentData.set(storedData);
      this.setUserId(storedData.email);
      this.trackEvent('user_restored_from_storage', {
        event_name: storedData.eventNameIdentifier,
        wants_notifications: storedData.sendEventReportNotification,
      });
    }
  }

  private _startSession(): void {
    if (!this._window) {
      return;
    }

    this._sessionStartTime.set(Date.now());
    this._window.gtag('event', 'session_start', {
      ...this._userParams(),
      timestamp: new Date().toISOString(),
      entry_point: this._window.location.href,
      referrer: this._document.referrer,
      is_returning_user: !!this._userId(),
    });
  }

  private _trackSessionEnd(): void {
    if (!this._window) {
      return;
    }

    const sessionStartTime = this._sessionStartTime();
    if (sessionStartTime) {
      const sessionDuration = (Date.now() - sessionStartTime) / 1000;
      this._window.gtag('event', 'session_end', {
        ...this._userParams(),
        session_duration: sessionDuration,
        exit_page: this._window.location.href,
        timestamp: new Date().toISOString(),
        is_returning_user: !!this._userId(),
      });
    }
  }

  private _getTimeOnPage(): number {
    const sessionStartTime = this._sessionStartTime();
    return sessionStartTime ? Date.now() - sessionStartTime : 0;
  }
}
